import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import UserLayout from '@components/layout/UserLayout.tsx';
import React from 'react';
import { CreateOrderResponse } from '@api/generated/search-service';
import FinishOverStatus from '@features/basket/components/step4/FinishOverStatus.tsx';
import { ThankYouBoxSuccess } from '@features/basket/components/step4/ThankYouBoxSuccess.tsx';
import { ThankYouBoxError } from '@features/basket/components/step4/ThankYouBoxError.tsx';
import dayjs from 'dayjs';
import { formatMoney } from '@utils/formats.ts';
import { Button } from '@components/ui/Button.tsx';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';
import { useTranslation } from 'react-i18next';

type OrderSuccessLocationState = {
  orderResult?: CreateOrderResponse;
};

export const Route = createFileRoute('/c/$orgId/$portalId/order-success/')({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const orgId = useOrgPortal(s => s.orgId);
  const portalId = useOrgPortal(s => s.portalId);
  const { t } = useTranslation('client');

  // Získat výsledek z navigation state
  const state = location.state as OrderSuccessLocationState;
  const orderResult = state?.orderResult || null;

  if (!orderResult) {
    return (
      <UserLayout containerClassName="bg-white">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">{t('common.error')}</h2>
            <p>{t('orderSuccess.loadError')}</p>
          </div>
        </div>
      </UserLayout>
    );
  }

  const isSuccess = orderResult.orders.filter(t => t.status === 'ERROR').length === 0;

  const handleBackToCatalog = () => {
    navigate({
      to: '/c/$orgId/$portalId/catalog',
      params: { orgId, portalId },
      search: {
        supplierFilterLevel: undefined,
        selectedSupplierIds: undefined,
      },
    });
  };

  return (
    <UserLayout containerClassName="bg-white">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <FinishOverStatus success={isSuccess} />

          {/* Výsledky pro jednotlivé dodavatele */}
          <div className="space-y-6 mb-8">
            {orderResult.orders.map((result, resultIndex) => (
              <React.Fragment key={resultIndex}>
                {result.status === 'SENT' ? (
                  <ThankYouBoxSuccess
                    contacts={result.supplierContacts}
                    supplierName={result.supplierName}
                    orderNumber={result.orderNumber!!}
                    orderId={result.orderId}
                    deliveryDate={dayjs(result.deliveryDate!!)}
                    totalWithoutTax={formatMoney(result.totalWithoutVat!!)}
                    totalWithTax={formatMoney(result.totalWithVat!!)}
                  />
                ) : (
                  <ThankYouBoxError supplierName={result.supplierName} errors={result.errors} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Akce */}
          <div className="flex justify-center">
            <Button label={t('orderSuccess.backToCatalog')} variant="fill" type={'primary'} onClick={handleBackToCatalog} className="w-full sm:w-auto" size="md" />
          </div>
        </div>
      </div>
    </UserLayout>
  );
}
